// ===========================================================================
// Regulars, client side: the bell, the notice toast, and the panel.
//
// A regular is someone you have both waved at after a chat. You stay
// anonymous to each other: the panel shows the handle you saw them use, when
// you became regulars, and -- only when they are in a room you could walk
// into yourself -- which room. Never a "last seen", never a status line,
// never anything they did not do in front of you.
//
// The panel is a modal in the shape of the account panel. It is rendered
// from whatever REGULARS the server last sent; app.js hands it each new one
// and it redraws. With no socket (opened from the landing) it explains itself
// and waits. The copy says exactly what a wave and a block do, because the
// honest version is the whole point of the feature. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el } from '../dom.js';
import { createCallButton } from './call-sheets.js'; // calls (5.B4)
import { createThreadView } from './messages.js';
import { STAFF_PERSON, STAFF_NAME, REGULAR_NICKNAME_MAX_CHARS, noteLimitFor, notePreview } from '@lumi/shared';
import { STAFF_ROW, hasStaffThread, staffUnread } from '../staff-inbox.js';

const BELL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';

/** What the pinned Lumi Staff row says under the name. Staff cannot be written to first, so it says what a person can do. */
export const STAFF_ROW_COPY = 'From the Lumi team. You can reply.';
/** The copy under the Wave control. One sentence of what it is, one of what it is not. */
export const WAVE_COPY = 'Wave if you would like to see them again. A regular is someone you have both waved at; you stay anonymous to each other.';
/** What a person is told when the other side had no identity to remember them by. */
export const NO_IDENTITY_COPY = 'They are not remembering anyone today.';
/**
 * How long the Wave control stays next to the searching status.
 *
 * Twenty seconds, and it was missed: a chat that had run for two hours ended
 * and the one chance to say "I would like to see them again" was gone before
 * the person had finished reading it. The whole regulars feature hangs off
 * this one button, and the cost of leaving it up longer is a button nobody
 * presses. Ninety seconds, and it no longer vanishes while being read --
 * clearWave now only runs on the next match or an explicit dismissal.
 */
// Four minutes. It was ninety seconds, which sounds generous until you watch
// somebody actually use it: the chat ends, they read what a wave IS, they
// decide, and by then it has gone. It is the only moment this feature has --
// miss it and two people who talked for two hours have no way back to each
// other -- and it costs nothing to leave on screen, because it sits in the
// searching overlay that is already there and has its own dismissal.
export const WAVE_OFFER_MS = 240_000;
/** The hours a quiet-hours window can start or end on. */
export const HOURS = Array.from({ length: 24 }, (_, i) => i);
/** What the pickers show before anything is chosen: a night, which is what
 *  almost everyone means by quiet hours. */
export const DEFAULT_QUIET = Object.freeze({ from: 22, to: 7 });

/**
 * "regular since <date>" -- a calendar date, never a time: the moment is not
 * the point, and a timestamp would read as surveillance of a friendship.
 * @param {string} iso
 * @param {number} [nowMs]  for tests
 */
export function formatSince(iso, nowMs = Date.now()) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return 'regular';
  const days = Math.floor((nowMs - ms) / 86_400_000);
  if (days <= 0) return 'regular since today';
  if (days === 1) return 'regular since yesterday';
  if (days < 7) return `regular since ${days} days ago`;
  const d = new Date(ms);
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `regular since ${month} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * One line of status for a regular's row: where they are if you could join,
 * else whether they are around at all. Invisible people arrive here as
 * neither, which is the whole of what invisible means.
 * @param {{ connected?: boolean, around?: { name?: string }|null }} r
 * @returns {{ text: string, kind: 'room'|'around'|'none' }}
 */
export function regularStatus(r) {
  if (r && r.around) return { text: `in ${r.around.name || 'a room'}`, kind: 'room' };
  if (r && r.connected) return { text: 'around', kind: 'around' };
  return { text: '', kind: 'none' };
}

/**
 * What a WAVED answer means on screen. `null` means show nothing.
 * @param {{ ok: boolean, reason?: string }} m
 */
export function waveResultText(m) {
  if (!m) return null;
  if (m.ok) return 'Waved.';
  switch (m.reason) {
    case 'no_identity':
      return NO_IDENTITY_COPY;
    case 'expired':
      return 'That chat was a while ago.';
    case 'rate_limited':
      return 'Slow down a moment.';
    default:
      return null; // unknown_pair, not_available, bad_request: nothing to say
  }
}

/**
 * The bell: a button with an unread dot. One per screen that has a header
 * (the 1:1 chat, the rooms hub); app.js keeps them in step.
 *
 * A message from a regular used to look like everything else: the same
 * four-pixel dot as "somebody is around", and a toast that was gone in six
 * seconds. So a message waited unseen (the operator, 2026-09-23: "it's not
 * very evident when someone has a message from a regular"). A message now
 * puts the NUMBER on the bell and keeps it there until the thread is opened,
 * because unlike a notice it is still true tomorrow.
 *
 * @param {{ onOpen: () => void }} h
 * @returns {{ root: HTMLElement, api: { setUnread: (n: number, opts?: { messages?: number }) => void } }}
 */
export function createBell({ onOpen }) {
  const dot = el('span', { class: 'bell-dot', 'aria-hidden': 'true' });
  const root = el('button', {
    type: 'button',
    class: 'btn btn-icon btn-ghost bell',
    title: t('Your regulars'),
    'aria-label': 'Your regulars',
    onClick: () => onOpen?.(),
  }, [el('span', { html: BELL_SVG }), dot]);
  return {
    root,
    api: {
      setUnread(n, { messages = 0 } = {}) {
        const msgs = Math.max(0, Number(messages) || 0);
        const on = Number(n) > 0 || msgs > 0;
        root.classList.toggle('has-unread', on);
        root.classList.toggle('has-message', msgs > 0);
        // Nine is where a count stops being a count and starts being "a lot".
        dot.textContent = msgs > 0 ? (msgs > 9 ? '9+' : String(msgs)) : '';
        const said = msgs > 0
          ? `${msgs} new message${msgs === 1 ? '' : 's'} from your regulars`
          : on ? 'Your regulars, something new' : 'Your regulars';
        root.setAttribute('aria-label', said);
        root.title = said;
      },
    },
  };
}

let noticeTimer = null;
/**
 * A toast with room for one action. dom.js's toast is text only, and a notice
 * that a regular is in a room is worth a Join button -- so this is its own
 * element, styled like the toast, never stacking (a new one replaces the last).
 *
 * The action is offered whenever the caller passes one. It used to be gated on
 * `notice.roomId` as well, which quietly dropped the button from every notice
 * carrying only a `code`: a community session about to start said so and gave
 * no way to go, which is the one notice where going is the whole point.
 *
 * @param {{ text: string, roomId?: string, code?: string }} notice
 * @param {{ onAction?: (n: object) => void, actionLabel?: string, ms?: number }} [opts]
 *   `actionLabel` because the action is not always joining: the owner of a
 *   community being told somebody asked to join needs the panel where they can
 *   say yes, not the room.
 */
export function showNoticeToast(notice, { onAction = null, actionLabel = 'Join', ms = 6000 } = {}) {
  document.querySelector('.notice-toast')?.remove();
  const kids = [el('span', { class: 'notice-text', text: String(notice.text || '') })];
  if (typeof onAction === 'function') {
    kids.push(
      el('button', {
        type: 'button',
        class: 'btn btn-primary btn-xs',
        text: actionLabel,
        onClick: () => {
          node.remove();
          onAction(notice);
        },
      }),
    );
  }
  const node = el('div', { class: 'toast notice-toast', role: 'status' }, kids);
  document.body.append(node);
  void node.offsetWidth;
  node.classList.add('show');
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => node.remove(), ms);
  return node;
}

/**
 * Open the regulars panel.
 * @param {object} h
 * @param {{ regulars: Array<object>, invisible: boolean, quietHours?: object|null }} h.state  the last REGULARS, or an empty one
 * @param {boolean} h.connected  whether there is a socket to act through
 * @param {number} [h.count]     the landing's count, when there is no list yet
 * @param {(personId: string) => void} [h.onBlock]
 * @param {(personId: string) => void} [h.onUnblock]  lifts a block; absent hides the list of people blocked
 * @param {(personId: string) => void} [h.onRemoveRecent]  takes somebody off the list of people talked to; absent hides the button
 * @param {(personId: string, mode: 'video'|'audio', handle: string|null) => void} [h.onCall]  calls (5.B4); absent hides the button
 * @param {boolean} [h.canVideo]  whether this band may ask for video (the chooser offers Voice only when not)
 * @param {(around: { roomId: string, code?: string }) => void} [h.onJoin]
 * @param {(invisible: boolean) => void} [h.onInvisible]
 * @param {(quietHours: { from: number, to: number, offset: number }|null) => void} [h.onQuietHours]
 *   absent hides the control entirely
 * @param {() => void} [h.onRefresh]
 * @param {() => void} [h.onClose]
 * @param {{ list: Function, send: Function, remove: Function, report: Function, newId: Function }|null} [h.messages]
 *   the socket calls for messages between regulars (ui/messages.js); null when the feature is off
 * @param {{ list: Function, send: Function, newId: Function }|null} [h.staff]
 *   the HTTP calls for the conversation with Lumi Staff. It does not depend on `connected`, on the
 *   band's switches, or on there being any regulars: `state.staff` ({ count, unread, lastAt }) is
 *   what makes the pinned row, and a count of 0 or null draws nothing at all
 * @returns {{ modal: HTMLElement, api: { update: (state: object) => void, close: () => void, messages: object|null, staff: object|null, openStaff: () => void } }}
 */
export function openRegulars(h) {
  let state = h.state || { regulars: [], invisible: false, recent: [], blocked: [] };
  /**
   * Whether there is a list to draw. Read on EVERY redraw, never captured
   * when the panel opened.
   *
   * The landing opens this before its HTTP list has arrived -- an account
   * holder can open it from the account panel the moment the page loads,
   * before either fetch has answered -- and a panel that latched "nothing
   * yet" went on saying so after the list landed. There was no way back:
   * Refresh hangs off the same answer, so it was not drawn either, and
   * somebody with three regulars sat looking at "start a chat to see your
   * regulars" until they thought to close the panel and open it again (the
   * operator, 2026-09-23, signed in and with three).
   */
  const haveList = () =>
    !!h.connected ||
    (Array.isArray(state.regulars) && state.regulars.length > 0) ||
    (Array.isArray(state.recent) && state.recent.length > 0);
  // The list and the thread are two children of the body, so a redraw of the
  // list (every REGULARS) never disturbs a thread somebody is reading.
  const listWrap = el('div', { class: 'regulars-list-wrap' });
  const body = el('div', { class: 'billing-body regulars-body' }, [listWrap]);
  const modal = el('div', { class: 'billing-modal account-modal regulars-modal', role: 'dialog', 'aria-label': 'Your regulars' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('Your regulars') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => close() }),
    ]),
    body,
    el('div', {
      class: 'billing-foot muted',
      text: t('A regular is someone you have both waved at after a chat. Lumi keeps the two of you, the names you saw, the date, any messages you send each other for thirty days, and any private note you keep about them. Nothing else: no history, and never where anyone was.'),
    }),
  ]);

  // ---- messages (ui/messages.js): the thread view, mounted once ----
  // Mounted when there are messages between regulars, or when Lumi Staff has
  // written (h.staff, and a thread to show). A person with neither never gets
  // one built: the staff thread is only ever added to, never imposed.
  let thread = null;
  const hasStaffRow = (s) => !!h.staff && hasStaffThread(s?.staff);
  function mountThread() {
    thread = createThreadView({
      onBack: () => {
        listWrap.hidden = false;
        modal.classList.remove('thread-open');
        h.onRefresh?.(); // the badges may have changed while reading
      },
      onList: (withPersonId, before) => h.messages?.list(withPersonId, before),
      onSend: (to, text, id, slow) => h.messages?.send(to, text, id, slow),
      onDelete: (id) => h.messages?.remove(id),
      onReport: (id, reason) => h.messages?.report(id, reason),
      newId: h.messages?.newId || h.staff?.newId,
      staff: h.staff || null,
    });
    body.append(thread.root);
  }
  if (h.messages || hasStaffRow(state)) mountThread();
  function openThread(r) {
    if (!thread) return;
    listWrap.hidden = true;
    // On a phone the thread takes the whole screen (styles: .thread-open).
    modal.classList.add('thread-open');
    thread.api.open(r);
  }
  // ---- end messages ----

  // ---- private notes (shared regular-notes.js): the editor, a view of its own ----
  // A sibling of the list like the thread, so a redraw of the list -- which
  // happens every time somebody comes or goes -- never takes the words out
  // from under somebody typing. Only the person writing ever sees a note; the
  // editor says so, and says that moderators can read them, because they can.
  let noteView = null;
  const NOTE_ERRORS = {
    refused: 'That note was not kept. Notes follow the same rules as messages: no contact details, links, or anything unsafe.',
    not_regulars: 'You are not regulars any more, so there is nothing to keep a note about.',
    rate_limited: 'That is a lot of saving. Try again in a few minutes.',
  };
  /** The longest note this person may keep: shorter in 13-17 (app.js knows the band). */
  const noteLimit = () => (typeof h.notes?.maxChars === 'function' ? h.notes.maxChars() : noteLimitFor(null));
  function closeNote() {
    noteView?.remove();
    noteView = null;
    listWrap.hidden = false;
  }
  function openNote(r) {
    if (!h.notes || typeof h.notes.save !== 'function') return;
    closeNote();
    const who = r.nickname || r.handle || t('Someone you talked to');
    const limit = noteLimit();
    const nick = el('input', {
      type: 'text',
      class: 'regular-note-nickname',
      maxlength: String(REGULAR_NICKNAME_MAX_CHARS),
      placeholder: r.handle || t('A name to know them by'),
      autocomplete: 'off',
      'aria-label': t('What you call them'),
    });
    nick.value = r.nickname || '';
    const area = el('textarea', {
      class: 'regular-note-input',
      rows: '4',
      maxlength: String(limit),
      placeholder: t('What do you want to remember about them? What did you talk about?'),
      'aria-label': `Your note about ${who}`,
    });
    area.value = r.note || '';
    const count = el('span', { class: 'muted regular-note-count', 'aria-live': 'polite' });
    const showCount = () => {
      count.textContent = `${area.value.length}/${limit}`;
    };
    area.addEventListener('input', showCount);
    showCount();
    const error = el('div', { class: 'regular-note-error', role: 'alert' });
    const save = el('button', { type: 'button', class: 'btn btn-primary btn-sm', text: t('Save note') });
    const remove = r.note || r.nickname ? el('button', { type: 'button', class: 'btn btn-ghost btn-sm', text: t('Delete note') }) : null;
    async function submit(nickname, text) {
      save.disabled = true;
      if (remove) remove.disabled = true;
      error.textContent = '';
      const out = await Promise.resolve(h.notes.save(r.personId, { nickname, text })).catch(() => null);
      save.disabled = false;
      if (remove) remove.disabled = false;
      if (out && out.ok) {
        closeNote();
        return;
      }
      error.textContent = out?.reason === 'too_long' && out.limit
        ? `Keep it to ${out.limit} characters.`
        : NOTE_ERRORS[out?.reason] || 'That note did not save. Try again.';
    }
    save.addEventListener('click', () => submit(nick.value, area.value));
    remove?.addEventListener('click', () => {
      if (!window.confirm(`Delete your note about ${who}?`)) return;
      submit('', '');
    });
    noteView = el('div', { class: 'regular-note-view' }, [
      el('div', { class: 'regular-note-head' }, [
        el('button', { type: 'button', class: 'btn btn-ghost btn-sm', text: t('Back'), onClick: closeNote }),
        el('strong', { text: `Note about ${who}` }),
      ]),
      el('p', { class: 'muted regular-note-help', text: t('Only you can see this note. The person it is about never will. Lumi moderators can read notes to keep everyone safe.') }),
      el('label', { class: 'regular-note-label' }, [el('span', { text: t('What you call them') }), nick]),
      el('label', { class: 'regular-note-label' }, [el('span', { text: t('Your note') }), area]),
      el('div', { class: 'regular-note-meta' }, [
        count,
        el('span', { class: 'muted', text: t('No real names, addresses, schools, or ways to contact them.') }),
      ]),
      error,
      el('div', { class: 'regular-note-actions' }, [save, remove].filter(Boolean)),
    ]);
    listWrap.hidden = true;
    body.append(noteView);
    area.focus();
  }
  // ---- end private notes ----

  function close() {
    thread?.api.close();
    closeNote();
    dialog.close();
    h.onClose?.();
  }

  function invisibleSwitch() {
    const btn = el(
      'button',
      {
        type: 'button',
        class: 'timed-toggle invisible-toggle',
        role: 'switch',
        aria: { checked: state.invisible ? 'true' : 'false' },
        disabled: h.connected ? null : '',
        onClick: () => {
          const next = !state.invisible;
          state = { ...state, invisible: next }; // optimistic; the server's REGULARS confirms
          btn.setAttribute('aria-checked', next ? 'true' : 'false');
          h.onInvisible?.(next);
        },
      },
      [
        el('span', { class: 'timed-box', 'aria-hidden': 'true' }),
        el('span', { class: 'timed-label' }, [
          el('strong', { text: t('Invisible') }),
          el('span', { class: 'timed-hint', text: t('Your regulars will not see which room you are in, or that you are here at all.') }),
        ]),
      ],
    );
    return btn;
  }

  /**
   * Quiet hours: the window in which nothing rings. The server has gated every
   * notice on this from the start and had no way to be told about it -- there
   * was no screen for it anywhere -- so it was a setting nobody could set.
   *
   * Hours on the PERSON'S clock, plus their offset, because the server cannot
   * guess a timezone and should not try. `from === to` is how the server spells
   * "none", so the switch sends null rather than a zero-length window.
   */
  function quietHoursControl() {
    const q = state.quietHours || null;
    const hourSelect = (name, value) =>
      el(
        'select',
        {
          class: 'quiet-hour',
          name,
          'aria-label': name === 'from' ? 'Quiet from' : 'Quiet until',
          disabled: q && h.connected ? null : '',
          onChange: () => send(),
        },
        HOURS.map((hh) =>
          el('option', { value: String(hh), text: `${String(hh).padStart(2, '0')}:00`, selected: hh === value ? '' : null }),
        ),
      );
    const fromSel = hourSelect('from', q ? q.from : DEFAULT_QUIET.from);
    const toSel = hourSelect('to', q ? q.to : DEFAULT_QUIET.to);
    const summary = el('span', { class: 'timed-hint' });

  
  function draw() {
      const on = !!state.quietHours;
      toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      fromSel.disabled = !on || !h.connected;
      toSel.disabled = !on || !h.connected;
      summary.textContent = on
        ? 'Nothing will ring in this window. Messages and requests still arrive; you just find them yourself.'
        : 'Pick a window in which nothing rings. Off by default.';
    }

    function send() {
      if (!state.quietHours) return;
      const from = Number(fromSel.value);
      const to = Number(toSel.value);
      // The server reads from === to as "no quiet hours", which would silently
      // turn the switch off under the person. Say so instead.
      if (from === to) {
        summary.textContent = 'Pick two different hours: a window that starts and ends at the same time is not a window.';
        return;
      }
      const next = { from, to, offset: -new Date().getTimezoneOffset() };
      state = { ...state, quietHours: next }; // optimistic; REGULARS confirms
      h.onQuietHours?.(next);
    }

    const toggle = el(
      'button',
      {
        type: 'button',
        class: 'timed-toggle quiet-toggle',
        role: 'switch',
        aria: { checked: state.quietHours ? 'true' : 'false' },
        disabled: h.connected ? null : '',
        onClick: () => {
          if (state.quietHours) {
            state = { ...state, quietHours: null };
            draw();
            h.onQuietHours?.(null);
            return;
          }
          const next = { from: Number(fromSel.value), to: Number(toSel.value), offset: -new Date().getTimezoneOffset() };
          state = { ...state, quietHours: next };
          draw();
          h.onQuietHours?.(next);
        },
      },
      [
        el('span', { class: 'timed-box', 'aria-hidden': 'true' }),
        el('span', { class: 'timed-label' }, [el('strong', { text: t('Quiet hours') }), summary]),
      ],
    );
    draw();
    return el('div', { class: 'quiet-hours' }, [
      toggle,
      el('div', { class: 'quiet-range' }, [
        el('span', { class: 'muted', text: t('From') }),
        fromSel,
        el('span', { class: 'muted', text: 'until' }),
        toSel,
      ]),
    ]);
  }

  /**
   * People you talked to this week, and a Wave for each.
   *
   * The site has always said a wave waits seven days for the other one. For
   * the person doing the waving that was not true: the only button that sent
   * one lived for twenty seconds beside a spinner, and a two-hour conversation
   * could end with no way to say you would like another. This is the promise
   * kept -- the same wave, the same window, from a list.
   *
   * It never says who waved at YOU. An unanswered wave is private; you learn
   * of one only by sending your own and finding it was mutual.
   */
  function recentChats() {
    const list = (Array.isArray(state.recent) ? state.recent : []).filter(Boolean);
    if (!haveList() || typeof h.onWavePerson !== 'function' || !list.length) return el('div', { hidden: '' });
    const rows = list.map((r) => {
      const name = r.handle || t('Someone you talked to');
      const btn = el('button', {
        class: `btn btn-sm ${r.waved ? 'btn-ghost' : 'btn-secondary'} recent-wave`,
        type: 'button',
        disabled: r.waved ? '' : undefined,
        text: r.waved ? t('Waved') : t('Wave'),
        title: r.waved
          ? t('You have waved. If they wave back within seven days you become regulars.')
          : t('Wave at them. If they wave back within seven days you become regulars.'),
        onClick: () => {
          btn.disabled = true;
          btn.textContent = t('Waving...');
          h.onWavePerson(r.personId);
        },
      });
      // Off the list before the seven days are up: the list can get long
      // (the operator, 2026-10-08). The row goes at once; the server's list follows.
      const remove = typeof h.onRemoveRecent === 'function'
        ? el('button', {
            class: 'recent-remove',
            type: 'button',
            text: 'x',
            title: t('Remove from this list'),
            'aria-label': `Remove ${name} from this list`,
            onClick: () => {
              state = { ...state, recent: (state.recent || []).filter((x) => x && x.personId !== r.personId) };
              render();
              h.onRemoveRecent(r.personId);
            },
          })
        : null;
      return el('div', { class: 'recent-row' }, [
        el('span', { class: 'recent-name', text: name }),
        el('span', { class: 'recent-when muted', text: whenLeft(r.expiresAt) }),
        btn,
        remove,
      ].filter(Boolean));
    });
    return el('div', { class: 'recent-chats' }, [
      el('p', { class: 'recent-title', text: t('People you talked to this week') }),
      el('p', { class: 'muted recent-note', text: t('You can wave for seven days after a chat. If they wave too, you become regulars. Nobody is told about a wave that was not returned.') }),
      el('div', { class: 'recent-list' }, rows),
    ]);
  }

  /**
   * The people this person has blocked, each with an Unblock. A block used
   * to be for good: nothing in the panel could lift one (the operator,
   * 2026-09-29). Lifting it brings back nothing it removed -- the regulars
   * row, the waves and the messages are gone -- so a confirm says so. The
   * other person is told nothing, as they were told nothing of the block.
   * Works from the landing too: app.js has an HTTP door for it.
   */
  function blockedPeople() {
    const list = (Array.isArray(state.blocked) ? state.blocked : []).filter(Boolean);
    if (!haveList() || typeof h.onUnblock !== 'function' || !list.length) return el('div', { hidden: '' });
    const rows = list.map((b) => {
      const name = b.handle || t('Someone you talked to');
      const btn = el('button', {
        class: 'btn btn-ghost btn-sm blocked-unblock',
        type: 'button',
        text: t('Unblock'),
        title: t('Let them wave at you and be paired with you again. They are not told.'),
        onClick: () => {
          if (!window.confirm(`Unblock ${name}? They can wave at you and be paired with you again. What the block removed does not come back: to be regulars again you both chat and wave again. They are not told anything.`)) return;
          btn.disabled = true;
          btn.textContent = t('Unblocking...');
          h.onUnblock(b.personId);
        },
      });
      return el('div', { class: 'recent-row blocked-row' }, [
        el('span', { class: 'recent-name', text: name }),
        el('span', { class: 'recent-when muted', text: blockedWhen(b.at) }),
        btn,
      ]);
    });
    return el('div', { class: 'recent-chats blocked-people' }, [
      el('p', { class: 'recent-title', text: t('People you have blocked') }),
      el('p', { class: 'muted recent-note', text: t('Unblocking lets them wave at you and be paired with you again. It does not bring back what the block removed.') }),
      el('div', { class: 'recent-list' }, rows),
    ]);
  }

  /** "blocked 3 days ago", in whole days. */
  function blockedWhen(at) {
    const ms = Date.parse(at || '');
    if (!Number.isFinite(ms)) return '';
    const days = Math.floor((Date.now() - ms) / 86_400_000);
    if (days <= 0) return t('blocked today');
    if (days === 1) return t('blocked yesterday');
    return t('blocked {n} days ago', { n: days });
  }

  /** "6 days left", in whole days, because a countdown is not the point. */
  function whenLeft(expiresAt) {
    const ms = Date.parse(expiresAt || '') - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return '';
    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
    return days <= 1 ? t('today') : t('{n} days left', { n: days });
  }

  function row(r) {
    const status = regularStatus(r);
    const actions = [];
    // ---- messages (ui/messages.js): a Message button with the unread badge ----
    if (h.connected && h.messages && thread) {
      const unread = Number(r.unread) || 0;
      actions.push(
        el('button', {
          type: 'button',
          class: `btn btn-secondary btn-sm regular-message${unread ? ' has-unread' : ''}`,
          title: unread ? `${unread} new message${unread === 1 ? '' : 's'}` : 'Send them a message',
          'aria-label': unread ? `Message, ${unread} new` : 'Message',
          onClick: () => openThread(r),
        }, ['Message', unread ? el('span', { class: 'regular-unread', text: String(unread) }) : null]),
      );
    }
    // ---- end messages ----
    if (r.around && h.connected && typeof h.onJoin === 'function') {
      actions.push(el('button', { type: 'button', class: 'btn btn-secondary btn-sm', text: t('Join'), onClick: () => { h.onJoin(r.around); close(); } }));
    }
    // ---- Calls (5.B4): a Call button, lit only while they are connected. ----
    // The panel closes when a call is placed so the "Calling..." sheet has
    // the screen; the rules live in calls.js on the server.
    if (h.connected && typeof h.onCall === 'function') {
      actions.push(createCallButton(r, {
        canVideo: !!h.canVideo,
        onCall: (personId, mode, handle) => {
          h.onCall(personId, mode, handle);
          close();
        },
      }));
    }
    // ---- end calls ----
    const blockBtn = el('button', {
      type: 'button',
      class: 'btn btn-ghost btn-sm regular-block',
      text: t('Block'),
      disabled: h.connected ? null : '',
      title: t('Remove them from your regulars. They are not told.'),
      onClick: () => {
        const who = r.handle || 'this person';
        // A confirm, because it cannot be undone from here: the wave would have
        // to happen again, after another chat. The other person is told nothing.
        if (!window.confirm(`Block ${who}? You will no longer be regulars, and they cannot wave at you again. They are not told anything.`)) return;
        blockBtn.disabled = true;
        h.onBlock?.(r.personId);
      },
    });
    // ---- private notes: Add note / Edit note, from anywhere (HTTP) ----
    if (h.notes && typeof h.notes.save === 'function') {
      actions.push(el('button', {
        type: 'button',
        class: 'btn btn-ghost btn-sm regular-note-btn',
        text: r.note || r.nickname ? t('Edit note') : t('Add note'),
        title: t('A private note to help you remember who they are. Only you can see it.'),
        onClick: () => openNote(r),
      }));
    }
    actions.push(blockBtn);
    // The note's first line under the name, so people can be told apart at a
    // glance; the whole note on the tooltip and in the editor.
    const preview = notePreview(r.note);
    return el('div', { class: `regular-row status-${status.kind}`, dataset: { personId: r.personId } }, [
      el('span', { class: 'regular-dot', 'aria-hidden': 'true' }),
      el('div', { class: 'regular-main' }, [
        el('div', { class: 'regular-name' }, [
          // What this person calls them, where they gave a name; the name the
          // regular used, quieter, beside it, so the two can still be matched.
          el('strong', { text: r.nickname || r.handle || 'Someone you talked to' }),
          r.nickname && r.handle && r.handle !== r.nickname ? el('span', { class: 'muted regular-seen-as', text: r.handle }) : null,
          status.text ? el('span', { class: 'regular-status', text: status.text }) : null,
        ].filter(Boolean)),
        preview ? el('span', { class: 'regular-note-preview', title: r.note, text: preview }) : null,
        el('span', { class: 'muted regular-since', text: formatSince(r.since) }),
      ].filter(Boolean)),
      el('div', { class: 'regular-actions' }, actions),
    ]);
  }

  /**
   * Lumi Staff, pinned above everyone: a row only when staff has written, and
   * with none of a regular's controls -- no Block, no Call, no Join, no status
   * -- because it is not a regular. It does not need a socket or a band switch
   * (the thread is read and answered over HTTP), so it is drawn whatever
   * `connected` says and with no regulars at all.
   */
  function staffRow() {
    if (!hasStaffRow(state) || !thread) return null;
    const unread = staffUnread(state.staff);
    return el('div', { class: 'regular-row regular-staff', dataset: { personId: STAFF_PERSON } }, [
      el('span', { class: 'regular-dot', 'aria-hidden': 'true' }),
      el('div', { class: 'regular-main' }, [
        el('div', { class: 'regular-name' }, [
          el('strong', { text: t(STAFF_NAME) }),
          el('span', { class: 'staff-badge', title: t('Verified Lumi staff'), text: t('LUMI STAFF') }),
        ]),
        el('span', { class: 'muted regular-since', text: t(STAFF_ROW_COPY) }),
      ]),
      el('div', { class: 'regular-actions' }, [
        el('button', {
          type: 'button',
          class: `btn btn-secondary btn-sm regular-message staff-message${unread ? ' has-unread' : ''}`,
          title: unread ? `${unread} new message${unread === 1 ? '' : 's'} from Lumi Staff` : 'Read your messages from Lumi Staff',
          'aria-label': unread ? `Message Lumi Staff, ${unread} new` : 'Message Lumi Staff',
          onClick: () => openThread(STAFF_ROW),
        }, ['Message', unread ? el('span', { class: 'regular-unread', text: String(unread) }) : null]),
      ]),
    ]);
  }

  // Everything in this panel hangs off this device's key alone until the
  // person puts a name on it (server keepsake-auth.js): a browser that
  // forgets the key -- cleared data, a new phone, ninety days away -- comes
  // back as a stranger, and one tester's did (2026-09-26). app.js hands
  // onKeep only when that is so -- no account, a keepsake nobody has named
  // -- and the panel shows this only when there is something to lose.
  function keepNudge() {
    return el('div', { class: 'keep-nudge' }, [
      el('p', { class: 'muted', text: t('Your regulars live on this device only. Add a username and password and they stay with the name for good, on any device you sign in from.') }),
      el('button', { type: 'button', class: 'btn btn-secondary btn-sm', text: t('Keep this identity'), onClick: () => h.onKeep() }),
    ]);
  }

  function render() {
    listWrap.replaceChildren();
    const list = Array.isArray(state.regulars) ? state.regulars : [];
    const staff = staffRow();
    // With no list to put it at the top of, the row stands in a list of its own.
    if (staff && (!haveList() || !list.length)) listWrap.append(el('div', { class: 'regulars-list' }, [staff]));
    if (!haveList()) {
      const n = Number(h.count) || 0;
      // Somebody who has nothing but a note from staff is not told to go and find regulars.
      if (n || !staff) {
        listWrap.append(
          el('p', {
            class: 'muted',
            text: n
              ? `You have ${n} regular${n === 1 ? '' : 's'}. Start a chat or open the rooms to see who is around.`
              : 'Start a chat or open the rooms to see your regulars.',
          }),
        );
      }
    } else if (!list.length) {
      listWrap.append(
        el('div', { class: 'regulars-empty' }, [
          el('p', { text: t('No regulars yet.') }),
          el('p', { class: 'muted', text: t('When a chat ends you can wave. If the other person waves too, within seven days, you become regulars: you see each other here, and which public room the other is in. Nobody is told about a wave that was not returned.') }),
        ]),
      );
    } else {
      listWrap.append(el('div', { class: 'regulars-list' }, staff ? [staff, ...list.map(row)] : list.map(row)));
    }
    if (haveList() && typeof h.onKeep === 'function') listWrap.append(keepNudge());
    listWrap.append(recentChats());
    listWrap.append(blockedPeople());
    listWrap.append(invisibleSwitch());
    if (typeof h.onQuietHours === 'function') listWrap.append(quietHoursControl());
    // My community (ui/community.js): a node app.js built once, moved here on
    // every redraw. Inside the list wrap, so an open message thread hides it too.
    if (h.communityCard) listWrap.append(h.communityCard);
    if (haveList() && typeof h.onRefresh === 'function') {
      listWrap.append(el('div', { class: 'buy-row regulars-refresh' }, [el('button', { type: 'button', class: 'btn btn-ghost btn-sm', text: t('Refresh'), onClick: () => h.onRefresh() })]));
    }
  }

  render();
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'Your regulars' });
  return {
    modal,
    api: {
      update(next) {
        if (!next) return;
        state = {
          regulars: Array.isArray(next.regulars) ? next.regulars : [],
          invisible: !!next.invisible,
          quietHours: next.quietHours || null,
          recent: Array.isArray(next.recent) ? next.recent : [],
          blocked: Array.isArray(next.blocked) ? next.blocked : [],
          staff: next.staff || null,
        };
        // Staff wrote while the panel was open: the thread to read it in is built now.
        if (!thread && hasStaffRow(state)) mountThread();
        render();
      },
      close,
      // The thread view's api (ui/messages.js), for app.js to route MSG,
      // MSG_LIST, MSG_DELETED, and the scoped notices to. Null when off.
      messages: h.messages && thread ? thread.api : null,
      // The same view, for the conversation with Lumi Staff (STAFF_MSG, and
      // which thread is open). Null when app.js gave no way to read it.
      get staff() {
        return h.staff && thread ? thread.api : null;
      },
      /** Open the thread with Lumi Staff, building the view if nothing has needed it yet. */
      openStaff() {
        if (!h.staff) return;
        if (!thread) mountThread();
        openThread(STAFF_ROW);
      },
    },
  };
}

export default { createBell, openRegulars, showNoticeToast, formatSince, regularStatus, waveResultText, WAVE_COPY, NO_IDENTITY_COPY, WAVE_OFFER_MS, STAFF_ROW_COPY };
